import React, { useState } from 'react';
import dayjs from 'dayjs';

import { FormComponent, ModalComponent, UiComponent } from 'components';
import { getEventStatus, getEventStatusTone } from 'components/event/utils';
import { useIsMobile, usePageOption } from 'hooks';
import { EventQuery } from 'queries';
import { theme } from 'styles';
import * as M from 'styles/mobile';
import { EventItemType } from 'types';

const EVENT_STATUS_LABEL: Record<string, string> = {
  REVIEW: '검토 중',
  RECRUITING: '모집 중',
  RECRUITMENT_CLOSED: '모집 완료',
  COMPLETED: '진행 완료',
  CANCELLED: '취소',
};

const mobileTitle = {
  flex: 1,
  minWidth: 0,
  fontSize: 14,
  color: theme.color.text,
};

interface Props {
  onSelect: (event: EventItemType) => void;
  onClose: () => void;
}

/**
 * 연결할 봉사 선택 모달.
 * 제목이 비슷한 봉사가 많아 오연결이 쉬우므로 진행 일시·주최 기관·모집 상태·ID 를 함께 보여준다.
 */
function EventSelect({ onSelect, onClose }: Props) {
  const isMobile = useIsMobile();
  const pageState = usePageOption(10);
  const [title, setTitle] = useState('');
  const { data } = EventQuery.useGetAll({
    page: pageState.pageOption.page,
    size: pageState.pageOption.size,
    title,
  });

  const handleSearch = ({ keyword }: { keyword?: string }) => {
    setTitle(keyword ?? '');
    pageState.handlePage({ ...pageState.pageOption, page: 1 });
  };

  return (
    <ModalComponent.Portal>
      <ModalComponent.Background>
        <ModalComponent.Modal
          title="봉사 선택"
          handleClose={onClose}
          footer={<></>}
        >
          <div css={{ width: '720px', maxWidth: '100%' }}>
            <FormComponent.Form
              submit={handleSearch}
              css={{
                display: 'flex',
                gap: '0 8px',
                marginBottom: '12px',
              }}
            >
              <FormComponent.SearchInput
                name="keyword"
                placeholder="봉사명으로 검색해주세요"
                css={{ width: '100%' }}
              />
            </FormComponent.Form>

            {isMobile ? (
              <div css={M.list}>
                {(data?.data ?? []).map((event: EventItemType) => {
                  const status = getEventStatus(
                    event.eventStatus,
                    event.startDate,
                    event.endDate,
                    event.enrollStartDate,
                    event.enrollEndDate,
                  );
                  return (
                    <button
                      key={event.id}
                      type="button"
                      css={M.listItem}
                      onClick={() => onSelect(event)}
                    >
                      <div css={M.listRow}>
                        <p css={mobileTitle}>{event.title}</p>
                        <span css={M.tag(getEventStatusTone(status))}>
                          {status}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div css={{ maxHeight: '420px', overflowY: 'auto' }}>
                <UiComponent.Table
                  cols={['ID', '봉사명', '진행 일시', '주최 기관', '모집 상태']}
                  data={data?.data ?? []}
                  onClickRow={onSelect}
                  renderer={{
                    ID: ({ id }: EventItemType) => <>{id}</>,
                    봉사명: ({ title: eventTitle }: EventItemType) => (
                      <p css={{ textAlign: 'left' }}>{eventTitle}</p>
                    ),
                    '진행 일시': ({ startDate, endDate }: EventItemType) => (
                      <>
                        {dayjs(startDate).format('YYYY.MM.DD HH:mm')} ~{' '}
                        {dayjs(endDate).format('HH:mm')}
                      </>
                    ),
                    '주최 기관': ({ organizationName }: EventItemType) => (
                      <>{organizationName ?? '-'}</>
                    ),
                    '모집 상태': ({ eventStatus }: EventItemType) => (
                      <>{EVENT_STATUS_LABEL[eventStatus] ?? eventStatus}</>
                    ),
                  }}
                />
              </div>
            )}
            <UiComponent.Pagination
              {...pageState}
              totalPage={data?.totalPages || 0}
            />
          </div>
        </ModalComponent.Modal>
      </ModalComponent.Background>
    </ModalComponent.Portal>
  );
}

export default EventSelect;
